import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

import './SocialButtonsList.scss';
import SocialButtonsList from './SocialButtonsList';
import { useNotNullableContext } from '../../hooks/useNotNullableContext';
import { PageMetaContext } from '../../context/PageMetaContext';
import { filterByTypename } from '../../utils/graphql';
import generateHash from '../../utils/generateHash';
import { usePageOwner } from '../../hooks/usePageOwner';
import useBasePageQuery from '../../hooks/useBasePageQuery';
import { useIsMounted } from '../../hooks/useIsMounted';

export type FundraisingSocialButtonsProps = {
  message?: string;
  subject?: string;
};

const FundraisingSocialButtons: FunctionComponent<FundraisingSocialButtonsProps> = ({
  message,
  subject,
}) => {
  const result = useBasePageQuery();
  const isMounted = useIsMounted();

  const { pageType, pageSlug, currentUrl } = useNotNullableContext(
    PageMetaContext,
  );
  const { t } = useTranslation('common', { useSuspense: false });
  const { isPageOwner } = usePageOwner();

  if (!isMounted || !result.data?.page) {
    return null;
  }

  const { relationships, title, owner } = result.data.page;
  const beneficiaries = relationships?.beneficiaries;
  const charities = filterByTypename(beneficiaries?.nodes ?? [], 'Charity');

  const charityName = charities[0]?.name
    ? charities[0].name
    : t('charity.defaultName');

  const defaultMessage = `${message ||
    t('common.shareMessageGeneric', { charityName })}`;
  const emailUrl = `${currentUrl}?utm_source=Sharethis&utm_medium=${pageType}&utm_content=${pageSlug}&utm_campaign=pfp-email&utm_term=${generateHash()}`;
  let emailMessage: string;
  isPageOwner
    ? (emailMessage = t('fundraising.shareEmailPageOwner', {
        charityName,
        emailUrl,
      }))
    : (emailMessage = t('fundraising.shareEmailNonPageOwner', {
        name: owner?.name,
        charityName,
        emailUrl,
      }));
  let whatsappAndSmsMessage: string;
  isPageOwner
    ? (whatsappAndSmsMessage = t('fundraising.shareWhatsappSMSPageOwner', {
        charityName,
      }))
    : (whatsappAndSmsMessage = t('fundraising.shareWhatsappSMSNonPageOwner', {
        name: owner?.name,
        charityName,
      }));

  return (
    <SocialButtonsList
      subject={subject || title || t('common.defaultEmailSubject')}
      messages={{
        default: defaultMessage,
        twitter: isPageOwner
          ? t('fundraising.shareTwitterPageOwner', {
              charityName,
            })
          : t('fundraising.shareTwitterNonPageOwner', {
              name: owner?.name,
              charityName,
            }),
        email: `${t('common.greeting')},\n\n${emailMessage}\n\n${t(
          'common.thankYou',
        )},\n\n${isPageOwner ? owner?.name : ''}`,
        whatsapp: whatsappAndSmsMessage,
        sms: whatsappAndSmsMessage,
      }}
    />
  );
};

export default FundraisingSocialButtons;
